import {
	AUTH_KEY,
	AUTH_EXPIRATION_DATE_KEY,
	AUTH_SUBJECT_KEY,
	REFRESH_TOKEN_KEY,
	ACCESS_TOKEN_EXPIRATION_KEY,
	REFRESH_TOKEN_EXPIRATION_KEY,
	FEATURES_KEY,
	IS_SUBSCRIBED_KEY,
} from '@shared/auth';
import {
	USER_ROLE,
	FAVORITE_KEY,
	ACCESS_TOKEN_KEY,
	ACCESS_TOKEN_EXPIRE_AT_KEY,
	APP_USER_ID,
	APP_ID_KEY,
	UNSAVED_EVENTS_KEY,
	SESSION_ID_KEY,
	READER_ID_KEY,
	SETTINGS_STORAGE_KEYS,
} from '@/constants';

/**
 * Keys a deploy (a `version`/`app_key` bump) must never wipe.
 *
 * This is the fix for topic 4's headline bug: `useBoot()` clears
 * `localStorage` whenever the built version or app key changes, which is
 * every release -- and clearing it wholesale signed every paying subscriber
 * out on every deploy. Everything *not* in this set is treated as disposable
 * cache; everything in it is session/entitlement state that has to survive.
 *
 * Extracted out of `app/app.tsx` (2026-08-28) so the policy is testable
 * without mounting the whole `App` tree, and so it lives in the storage
 * layer rather than inside a component file. `app.tsx` imports it back --
 * behaviour is unchanged, this is a move plus a test, not a redesign.
 *
 * `FAVORITE_KEY` is deliberately in here despite not being auth: it is
 * user-authored content (the reader's saved articles), not a cache, and
 * losing it on a release is the same class of user-visible harm as being
 * logged out. The original call site hardcoded the string `'favorites'`
 * next to the imported auth constants; using the existing `FAVORITE_KEY`
 * constant (`constants.ts:14`, same value) removes the one place this set
 * could silently drift from the key favorites are actually stored under.
 */
export const PRESERVED_ON_DEPLOY_KEYS: ReadonlySet<string> = new Set([
	FAVORITE_KEY,
	AUTH_KEY,
	AUTH_EXPIRATION_DATE_KEY,
	AUTH_SUBJECT_KEY,
	REFRESH_TOKEN_KEY,
	ACCESS_TOKEN_EXPIRATION_KEY,
	REFRESH_TOKEN_EXPIRATION_KEY,
	FEATURES_KEY,
	IS_SUBSCRIBED_KEY,
	USER_ROLE,
	ACCESS_TOKEN_KEY,
	ACCESS_TOKEN_EXPIRE_AT_KEY,
	APP_USER_ID,
	// The app's id in `system`, fetched once and stamped on reader events.
	// It records which app it belongs to, so it is safe across an app-key change.
	APP_ID_KEY,
	// A deploy arriving mid-read must not split the reader's session in two.
	SESSION_ID_KEY,
	// Losing it on a release would turn every returning reader into a new one.
	READER_ID_KEY,
	// The last reading of a page that was closed; the event log picks it up
	// on the next launch, which may be the one a release reloads into.
	UNSAVED_EVENTS_KEY,
	// The reader's Settings (theme, motion, text, language, analytics
	// opt-out...): choices, not cache. Before 2026-09-26 every release reset
	// them.
	...SETTINGS_STORAGE_KEYS,
]);

/**
 * Clears every `localStorage` key except those in
 * `PRESERVED_ON_DEPLOY_KEYS`.
 *
 * Iterates a snapshot of the key list rather than `for (const key in
 * localStorage)` (the original form): mutating storage while enumerating it
 * is the classic skip-every-other-entry bug. `Object.keys` on the
 * `Storage` object returns only its own enumerable string keys -- the same
 * set `for...in` walked -- so this is a safety fix, not a scope change.
 */
export function clearDisposableLocalStorage(
	storage: Storage = localStorage,
): void {
	for (const key of Object.keys(storage)) {
		if (PRESERVED_ON_DEPLOY_KEYS.has(key)) {
			continue;
		}
		storage.removeItem(key);
	}
}
